import {
  Avatar,
  Box,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import React, { useState } from "react";
import FollowButton, { useFollowToggle } from "../../components/FollowButton";
import StandardDialog from "../../components/StandardDialog";
import { useAuth } from "../../context/authContext";
import UserSearch from "../explore/UserSearch";
import { useNavigate } from "react-router-dom";

/**
 * One tab's people, each opening their profile, with the same Follow /
 * Following shortcut as the user search — except on yourself, or when
 * signed out. Shows `emptyMessage` when there's nobody.
 */
function PeopleList({
  people,
  emptyMessage,
  onSelect,
  iconOnly,
  isFollowing,
  toggleFollow,
}) {
  const { user } = useAuth();

  if (people.length === 0) {
    return (
      <Box textAlign="center" py={2}>
        <Typography>{emptyMessage}</Typography>
      </Box>
    );
  }
  return (
    <List>
      {people.map((person) => (
        <ListItem
          key={person.id}
          sx={{
            // Room for the follow button (MUI reserves only 48px), so long
            // usernames don't run under it
            pr: iconOnly ? 7 : 17,
            // No left gutter: the ListItemButton's own padding alone puts the
            // avatar 16px in, matching the follow button's gap on the right
            pl: 0,
          }}
          secondaryAction={
            user &&
            person.id !== user.id && (
              <FollowButton
                following={isFollowing(person)}
                username={person.username}
                iconOnly={iconOnly}
                onClick={() => toggleFollow(person)}
              />
            )
          }
        >
          <ListItemButton onClick={() => onSelect(person.username)}>
            <ListItemAvatar>
              <Avatar
                src={person.avatar_url}
                imgProps={{ referrerPolicy: "no-referrer" }}
              />
            </ListItemAvatar>
            <ListItemText primary={person.username} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
}

/**
 * A profile's followers and following, plus profile search, in one dialog.
 * Tabs read "12 followers" / "3 following"; search is an icon tab.
 */
export default function NetworkDialog({ open, onClose, ...props }) {
  const theme = useTheme();
  const isBelowSm = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      topOffset={"10vh"}
      sx={{
        "& .MuiDialog-paper": {
          height: isBelowSm ? undefined : "300px",
        },
      }}
    >
      <NetworkDialogBody onClose={onClose} isBelowSm={isBelowSm} {...props} />
    </StandardDialog>
  );
}

/**
 * The dialog's contents, mounted fresh on each open (MUI unmounts a closed
 * Dialog's children). The lists are a snapshot from that moment: following or
 * unfollowing refetches the profile, and a live list would drop an unfollowed
 * row before you could undo it. They catch up on the next open.
 */
function NetworkDialogBody({
  onClose,
  isBelowSm,
  tabValue,
  setTabValue,
  profile,
  isOwnProfile,
}) {
  const navigate = useNavigate();
  const [{ followers, following }] = useState(() => ({
    followers: profile.followers,
    following: profile.following,
  }));
  // Shared by both tabs so a follow change survives switching between them
  const { isFollowing, toggleFollow } = useFollowToggle();

  const handleDialogSelect = (username) => {
    onClose();
    navigate(`/u/${username}`);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          selectionFollowsFocus
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            // Counts are live (unlike the rows), so they track follow changes
            label={`${profile.followers.length} ${profile.followers.length === 1 ? "follower" : "followers"}`}
            value="follower"
          />
          <Tab
            label={`${profile.following.length} following`}
            value="following"
          />
          <Tab
            icon={<SearchRoundedIcon fontSize="small" />}
            aria-label="Search profiles"
            value="search"
            // Icon tabs default wider than the icon needs
            sx={{ minWidth: 48 }}
          />
        </Tabs>
        <IconButton
          aria-label="close"
          size="small"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
            mr: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ p: 0 }}>
        {tabValue === "follower" ? (
          <PeopleList
            people={followers}
            onSelect={handleDialogSelect}
            iconOnly={isBelowSm}
            isFollowing={isFollowing}
            toggleFollow={toggleFollow}
            emptyMessage={
              isOwnProfile ? (
                "You have no followers yet"
              ) : (
                <>
                  <i>@{profile.username}</i> has no followers yet
                </>
              )
            }
          />
        ) : tabValue === "following" ? (
          <PeopleList
            people={following}
            onSelect={handleDialogSelect}
            iconOnly={isBelowSm}
            isFollowing={isFollowing}
            toggleFollow={toggleFollow}
            emptyMessage={
              isOwnProfile ? (
                "You are not following anyone yet"
              ) : (
                <>
                  <i>@{profile.username}</i> is not following anyone yet
                </>
              )
            }
          />
        ) : (
          tabValue === "search" && (
            <Box textAlign="center" p={2}>
              <UserSearch fullscreen={isBelowSm} />
            </Box>
          )
        )}
      </DialogContent>
    </>
  );
}
